import { isFieldGroupToReference } from '@modusign/react-signing';
import {
  CheckboxFieldGroupToReference,
  FieldNodeToReference,
  Position,
} from '@modusign/react-signing/types';
import cloneDeep from 'lodash/cloneDeep';

const CONTRACT_PROGRESS_FIELD_SORT_ADJUST_RANGE_RELATIVE_Y = 0.004;

function getPosition(field: FieldNodeToReference): Position | null {
  if (isFieldGroupToReference(field)) {
    if (field.children.length === 0) {
      return null;
    }

    let result = cloneDeep(getPosition(field.children[0]));

    field.children.forEach((child, i) => {
      if (i === 0) {
        return;
      }

      const position = getPosition(child);
      if (result === null) {
        result = position;
        return;
      }

      if (position) {
        result.x = Math.min(result.x, position.x);
        result.y = Math.min(result.y, position.y);
        result.z = Math.min(result.z, position.z);
        result.page = Math.min(result.page, position.page);
      }
    });
    return result;
  }
  return cloneDeep(field.position);
}

export function sortFields(fields: FieldNodeToReference[]): void {
  /**
   * 페이지 내 필드들에 대해서 정렬한다.
   * 1. 페이지 순으로
   * 2. 위에서 아래로
   * 3. 왼쪽에서 오른쪽
   * 4. 사인/도장 => 텍스트 => 체크박스 순
   */
  fields.sort((a, b) => {
    // 1. 페이지 순으로

    const aPosition = getPosition(a);
    const bPosition = getPosition(b);
    if (aPosition && bPosition) {
      if (aPosition.page !== bPosition.page) {
        return aPosition.page - bPosition.page;
      }

      // 2. 위에서 아래로
      /**
       * 서명요청 시, 사용자의 드래그로 필드가 놓여지므로 허용범위 내에서의
       * y좌표 차이는 같은 높이라고 판단하고 정렬하지 않도록 조정합니다.
       */

      const subAFromB = bPosition.y - aPosition.y;
      if (subAFromB > CONTRACT_PROGRESS_FIELD_SORT_ADJUST_RANGE_RELATIVE_Y) {
        return -1;
      }
      if (subAFromB < -CONTRACT_PROGRESS_FIELD_SORT_ADJUST_RANGE_RELATIVE_Y) {
        return 1;
      }
      // 3. 왼쪽에서 오른쪽
      if (aPosition.x !== bPosition.x) {
        return aPosition.x - bPosition.x;
      }

      // 4. 사인/도장 => 텍스트 => 체크박스 순으로
      if (a.type === b.type) {
        return 0;
      }
      if (a.type === 'SIGNATURE') {
        return -1;
      }
      if (b.type === 'SIGNATURE') {
        return 1;
      }
      if (a.type === 'CHECKBOX_GROUP' && b.type === 'TEXT') {
        return 1;
      }
      if (a.type === 'TEXT' && b.type === 'CHECKBOX_GROUP') {
        return -1;
      }
    }
    return 0;
  });

  fields
    .filter((field): field is CheckboxFieldGroupToReference => field.type === 'CHECKBOX_GROUP')
    .forEach((checkboxGroup) => {
      // TODO: (standardize-document-content): 아래 slice() 사용하는 방향에 대해 리서치 진행하기
      // FYI https://github.com/modusign/modusign-client/pull/6275#discussion_r863348017
      checkboxGroup.children.sort((a, b) => {
        if (a.type === 'CHECKBOX' && b.type === 'CHECKBOX') {
          return a.index - b.index;
        }
        return 0;
      });
    });
}

export function pureSortFields(fields: FieldNodeToReference[]): FieldNodeToReference[] {
  const copiedFields = cloneDeep(fields);
  sortFields(copiedFields);
  return copiedFields;
}
